// La tarjeta «Para Santi» (pelotas v6/v7; se va en la entrega 2 de la ventana «Santi» nueva, cuando el agente deje
// borradores en la caja): lo que el agente deja listo para que Pau lo envíe con UN clic. Dos sitios: dentro del chat del
// agente (`variante="chat"`: título, filas, «Cambiar algo» / «Enviar a Santi»; enviada, se queda diciendo a quién le
// toca y desde cuándo) y en el cajón «Tu agente» de la ventana con Santi (`variante="panel"`: el texto, «Usar en el chat»
// / «Enviar a Santi»; enviada, «✓ Enviado a Santi · 14:05»). Los datos salen de GET /agentes/:id (store.tarjetas), al día
// por el socket. 03-11: sin la pelota (ni P —— S ni «Le pasas la pelota»): la línea dice a quién le toca, en palabras.
// 02-11: un socio ve la tarjeta entera, pero enviarla o cambiarla es de Pau: en lugar de los botones, una línea que lo
// dice (`useHablaComoPau`; el servidor contesta 403 SOLO_PAU igualmente).
import { Check } from 'lucide-react';
import { useEffect, useState } from 'react';
import { t } from '../../../i18n';
import { cn } from '../../../lib/utils';
import type { ContratadoDTO } from '../compartido/vocabulario';
import { mensajeErrorPelotas } from '../errores';
import { Aviso, Boton } from '../ui';
import { AvatarPersona, LoEnviaPau } from './piezas';
import { useHablaComoPau, usePelotasStore, type EnviadoHaceNada } from './store';
import { hora, lleva, nombreDe, YO } from './tiempo';
import type { EnviadaDTO, FilaTarjeta, TarjetaDTO } from './tipos';

/** Lo que «Usar en el chat» deja en la caja de escribir del contrato (enviar ya le pasa el turno a Santi). */
export interface Relleno {
  texto: string;
}

const BOTON = 'h-11 px-5 text-[16px]';

function textoDe(filas: FilaTarjeta[], nombre: string): string {
  return filas.find((f) => f.nombre === nombre)?.texto ?? '';
}

export function rellenoDe(tj: Pick<TarjetaDTO, 'tipo' | 'filas'>): Relleno {
  // Un pase: lo hecho y, en otro párrafo, lo que le toca a Santi (la oficina deriva el turno del texto al enviar).
  if (tj.tipo === 'pasar') return { texto: [textoDe(tj.filas, 'Hecho'), textoDe(tj.filas, 'Os toca')].filter(Boolean).join('\n\n') };
  return { texto: tj.tipo === 'mensaje' ? textoDe(tj.filas, 'Mensaje') : tj.filas.map((f) => `${f.nombre}: ${f.texto}`).join('\n') };
}

/** Las tarjetas de un chat de agente: la que espera y las ya enviadas (el pie de ConversacionOficina). */
export function TarjetasDelChat({ contratado: c }: { contratado: Pick<ContratadoDTO, 'agentId'> }) {
  const tarjetas = usePelotasStore((s) => s.tarjetas[c.agentId]);
  const cargar = usePelotasStore((s) => s.cargarTarjetas);
  useEffect(() => {
    void cargar(c.agentId);
  }, [c.agentId, cargar]);
  if (!tarjetas || (!tarjetas.pendiente && !tarjetas.enviadas.length)) return null;
  return (
    <div className="space-y-2">
      {tarjetas.enviadas.map((e) => (
        <TarjetaEnviada key={e.temaId} e={e} />
      ))}
      {tarjetas.pendiente ? <TarjetaPendiente tj={tarjetas.pendiente} variante="chat" /> : null}
    </div>
  );
}

function Filas({ filas, compacta = false }: { filas: FilaTarjeta[]; compacta?: boolean }) {
  return (
    <dl className={cn('mt-3 grid gap-x-4 gap-y-1.5 text-[16px] leading-6', compacta ? 'grid-cols-[96px_minmax(0,1fr)]' : 'grid-cols-[112px_minmax(0,1fr)]')}>
      {filas.map((f) => (
        <div key={f.nombre} className="contents">
          <dt className="text-oficina-texto2">{f.nombre}</dt>
          <dd className="whitespace-pre-wrap break-words">{f.texto}</dd>
        </div>
      ))}
    </dl>
  );
}

function Rotulo({ texto }: { texto: string }) {
  return (
    <p className="flex items-center gap-2 text-[15px] font-semibold leading-5 text-oficina-texto2">
      <AvatarPersona persona="santi" tamano={24} />
      {texto}
    </p>
  );
}

/**
 * La tarjeta que espera el clic. `enviar` manda la tarjeta (o cierra el contrato); `cambiar` la devuelve al agente con
 * una pregunta («Cambiar algo» / «Seguir hablando»). Mientras va, los botones esperan; si falla, el motivo debajo.
 */
export function TarjetaPendiente({ tj, variante, alUsar }: { tj: TarjetaDTO; variante: 'chat' | 'panel'; alUsar?: (r: Relleno) => void }) {
  const decidir = usePelotasStore((s) => s.decidir);
  const comoPau = useHablaComoPau();
  const [enviando, setEnviando] = useState<'enviar' | 'cambiar' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const ocupada = tj.estado !== 'esperando' || enviando !== null;
  const cerrar = tj.boton === 'cerrar';
  const mensaje = tj.tipo === 'mensaje';
  const hacer = async (decision: 'enviar' | 'cambiar') => {
    setEnviando(decision);
    setError(null);
    try {
      await decidir(tj.agentId, tj.preguntaId, decision);
    } catch (e) {
      setError(mensajeErrorPelotas(e));
    } finally {
      setEnviando(null);
    }
  };
  const panel = variante === 'panel';
  // En el panel, un pase se lee de un tirón, como lo deja el agente (v7): «Hecho: … Os toca: …».
  const enParrafo = mensaje || (panel && tj.tipo === 'pasar');
  const texto = mensaje ? textoDe(tj.filas, 'Mensaje') : enParrafo ? tj.filas.map((f) => `${f.nombre}: ${f.texto}`).join(' ') : '';
  return (
    <section
      className={cn(
        'bg-oficina-elevada shadow-lg shadow-black/35 ring-1 ring-inset ring-white/10',
        panel ? 'mb-1 mt-2.5 rounded-[14px] px-4 pb-4 pt-3.5' : 'mb-1 ml-[42px] mt-2.5 w-[640px] max-w-[calc(100%-42px)] rounded-2xl px-6 pb-5 pt-[18px]',
      )}
      data-testid="tarjeta-para-santi"
      data-tipo={tj.tipo}
    >
      <Rotulo texto={t(cerrar ? 'oficinaV2.pelotas.tarjeta.contratoCon' : 'oficinaV2.pelotas.tarjeta.paraSanti')} />
      {panel && enParrafo ? (
        <p className="mt-2 whitespace-pre-wrap break-words text-[16px] leading-6 [text-wrap:pretty]" data-testid="tarjeta-texto">{texto}</p>
      ) : (
        <>
          <h3 className={cn('mt-2 font-semibold tracking-[-0.01em]', panel ? 'text-[18px] leading-6' : 'text-[22px] leading-7')}>{tj.titulo}</h3>
          {mensaje ? <p className="mt-2 whitespace-pre-wrap break-words text-[16px] leading-6" data-testid="tarjeta-texto">{texto}</p> : <Filas filas={tj.filas} compacta={panel} />}
        </>
      )}
      {panel && (tj.tipo === 'pasar' || tj.tipo === 'abrir') ? (
        <p className="mt-3 text-[15px] leading-5 text-oficina-texto2">{t('oficinaV2.pelotas.tarjeta.lePasas')}</p>
      ) : null}
      {!comoPau ? (
        <LoEnviaPau className={cn('justify-end text-right', panel ? 'mt-4' : 'mt-[22px]')} />
      ) : (
        <div className={cn('flex flex-wrap items-center justify-end', panel ? 'mt-4 gap-2' : 'mt-[22px] gap-3')} data-testid="tarjeta-acciones">
          {panel && alUsar && (mensaje || tj.tipo === 'pasar') ? (
            <Boton variante="secundario" className={cn(BOTON, 'px-4')} disabled={ocupada} onClick={() => alUsar(rellenoDe(tj))}>
              {t('oficinaV2.pelotas.tarjeta.usar')}
            </Boton>
          ) : panel ? null : (
            <Boton variante="secundario" className={BOTON} disabled={ocupada} cargando={enviando === 'cambiar'} onClick={() => void hacer('cambiar')}>
              {t(cerrar ? 'oficinaV2.pelotas.tarjeta.seguirHablando' : 'oficinaV2.pelotas.tarjeta.cambiar')}
            </Boton>
          )}
          <Boton variante="primario" className={cn(BOTON, panel && 'px-4')} icono={cerrar ? Check : undefined} disabled={ocupada} cargando={enviando === 'enviar' || tj.estado === 'aprobando'} onClick={() => void hacer('enviar')}>
            {t(cerrar ? 'oficinaV2.pelotas.tarjeta.cerrar' : 'oficinaV2.pelotas.tarjeta.enviar')}
          </Boton>
        </div>
      )}
      {error ? (
        <div className="mt-3">
          <Aviso tono="error">{error}</Aviso>
        </div>
      ) : null}
    </section>
  );
}

/** Enviada (v6 2-a-la-vista): la tarjeta se queda en el chat diciendo a quién le toca y desde cuándo. */
export function TarjetaEnviada({ e }: { e: EnviadaDTO }) {
  return (
    <section className="mb-1 ml-[42px] mt-2.5 w-[640px] max-w-[calc(100%-42px)] rounded-2xl bg-oficina-elevada px-6 pb-5 pt-[18px] shadow-lg shadow-black/35 ring-1 ring-inset ring-white/10" data-testid="tarjeta-enviada">
      <Rotulo texto={t('oficinaV2.pelotas.tarjeta.paraSanti')} />
      <h3 className="mt-2 text-[22px] font-semibold leading-7 tracking-[-0.01em]">{e.titulo}</h3>
      {e.filas.length ? <Filas filas={e.filas} /> : null}
      <p className="mt-[18px] border-t border-white/[0.08] pt-4 text-[16px] leading-6 text-oficina-texto2">
        <b className="font-semibold text-oficina-texto">{e.lado === YO ? t('oficinaV2.pelotas.tarjeta.teToca') : t('oficinaV2.pelotas.tarjeta.esperandoA', { quien: nombreDe(e.lado) })}</b> · {t('oficinaV2.pelotas.tarjeta.desdeHace', { lleva: lleva(e.desde) })}
      </p>
    </section>
  );
}

/** En el panel «Tu agente» (v7 2-general): lo que el agente redactó y Pau acaba de enviar. */
export function TarjetaEnviadaHaceNada({ e }: { e: EnviadoHaceNada }) {
  const texto = textoDe(e.filas, 'Mensaje');
  return (
    <section className="mb-1 mt-2.5 rounded-[14px] bg-oficina-elevada px-4 pb-4 pt-3.5 shadow-lg shadow-black/35 ring-1 ring-inset ring-white/10" data-testid="tarjeta-enviada-hace-nada">
      <Rotulo texto={t('oficinaV2.pelotas.tarjeta.paraSanti')} />
      {texto ? (
        <p className="mt-2 whitespace-pre-wrap break-words text-[16px] leading-6 [text-wrap:pretty]">{texto}</p>
      ) : (
        <>
          <h3 className="mt-2 text-[18px] font-semibold leading-6 tracking-[-0.01em]">{e.titulo}</h3>
          <Filas filas={e.filas} compacta />
        </>
      )}
      <p className="mt-3 flex items-center gap-2 border-t border-white/[0.08] pt-3 text-[15px] leading-5 text-oficina-texto2">
        <Check className="h-[18px] w-[18px] text-oficina-acento" strokeWidth={2.5} aria-hidden />
        {t('oficinaV2.pelotas.tarjeta.enviado', { hora: hora(e.en) })}
      </p>
    </section>
  );
}
